import * as Actions from '../actions/importSourceActions';
import ImportSource from '../../entities/importSource';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ImportSourceAction = { 
    type: string; 
    importSource: ImportSource | null; 
    importSourceId: string | ImportSource; 
    partial: boolean; 
    lastFetch: number; 
    importSources: ImportSource[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<ImportSource> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {ImportSource[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<ImportSource> | null, firmId: string, userId: string, updates?: ImportSource[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'importSource', 'importSources', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<ImportSource>} data
 * @param {ImportSource[]} [updates]
 * @returns {ArrayMap<ImportSource>}
 */
function updateStorage(data: ArrayMap<ImportSource>, updates?: ImportSource[]): ArrayMap<ImportSource> {
	if (logging) console.debug('⮂ importSourceReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {ImportSource[]} [updates]
 * @returns {ArrayMap<ImportSource>}
 */
function updateStorageNoReturn(updates?: ImportSource[]): void {
	if (logging) console.debug('⮂ importSourceReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {ImportSource[]} [data]
 * @returns {ArrayMap<ImportSource>}
 */
function createState(data?: ImportSource[] | ArrayMap<ImportSource>): ArrayMap<ImportSource> {
	if (!data) return ArrayMapRegistry.clone<ImportSource>('importSource', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<ImportSource>('importSource', data.map(e => e));
    else return ArrayMapRegistry.clone<ImportSource>('importSource', data);
}

function importSources(state: ArrayMap<ImportSource>, action: ImportSourceAction): ArrayMap<ImportSource> {
	if (!state) state = createState();

	let st: ArrayMap<ImportSource>;

	switch (action.type) {
		// action.importSource - the new value of importSource with id action.id
		// action.importSourceId - id of the target, used if new value is null
		case Actions.IMPORTSOURCE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ importSourceReducer: FETCH_ONE_SUCCESS', action);

			if (action.importSource === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.importSourceId)) {
				st = state.clone();
				st.set(action.importSource);
				// state.clear();

				updateStorageNoReturn([action.importSource]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.importSources
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.importSources
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.importSources
			 !partial, !stateEmpty, fetchEmpty	-> D return importSources in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge importSources in state newer than those in action.importSources
		 */
		case Actions.IMPORTSOURCE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.importSources.length === 0;
			const fetched = action.importSources.map(importSource => new ImportSource({...importSource, persisted: true}));

			if (logging) console.debug(`⮂ importSourceReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} importSources with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ importSourceReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ importSourceReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<ImportSource> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: ImportSource[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(importSource => {
					const current = st.get(importSource.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && importSource.status === 'D') {
							// Remove it from state
							toUpdate.push(importSource);
							st.delete(importSource);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (importSource.addedOn > current.addedOn) {
								toUpdate.push(importSource);
								st.set(importSource);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (importSource.status !== 'D') {
							toUpdate.push(importSource);
							st.set(importSource);
						}
						else if (logging) console.debug('⮂ importSourceReducer: partialMerge: skipping deleted importSource not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ importSourceReducer: empty: importSources', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ importSourceReducer: fullMerge: importSources', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localImportSource => {
                    // If this importSource has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localImportSource.persisted) {
                        const fetchedImportSource = st.get(localImportSource.id);
                        if (!fetchedImportSource || localImportSource.addedOn > fetchedImportSource.addedOn) st.set(localImportSource);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.IMPORTSOURCE_FETCH_ONE_FAILURE:
			console.error('IMPORTSOURCE_FETCH_ONE_FAILURE', action.importSourceId, action.err);

			// No update to storage required
			return state;

		case Actions.IMPORTSOURCE_FETCH_FAILURE:
			console.error('IMPORTSOURCE_FETCH_FAILURE', action.importSourceId, action.err);

			// No update to storage required
			return state;

		case Actions.IMPORTSOURCE_RESTORE: {
		    let importSource = new ImportSource({...action.importSource});
            st = state.clone();

            const existing = st.get(importSource.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (importSource.addedOn >= existing.addedOn) {
                    st.set(importSource);
                    return updateStorage(st, [importSource]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (importSource.status === 'D') return state;

                st.set(importSource);
                return updateStorage(st, [importSource]);
            }
        }
            
		case Actions.IMPORTSOURCE_SAVE: {
		    // console.debug('⮂ IMPORTSOURCE_SAVE reducer', action.importSource?.id);
		    
			// clone the importSource
			let importSource = new ImportSource({...action.importSource});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new importSource in the state
			// If this is a create, add to the queue
			const _importSource = st.get(importSource.id);

			if (!_importSource) st.set(importSource);
			else {
				if (importSource.addedOn >= _importSource.addedOn) st.set(importSource);
			}

			return updateStorage(st, [importSource]);
		}

		case Actions.IMPORTSOURCE_SAVE_MANY: {
		    if (logging) console.debug('⮂ IMPORTSOURCE_SAVE_MANY reducer', action.importSources.map(importSource => importSource.id).join(','));
		    
			// clone the importSources
			let toProcess = action.importSources.map(importSource => new ImportSource(importSource));

			st = state.clone();
			// state.clear();

			const updates: ImportSource[] = [];

			toProcess.forEach(importSource => {
				// Add or update the new importSource in the state
				// If this is a create, add to the queue
				const existing = st.get(importSource.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (importSource.status !== 'D') {
						st.push(importSource);
						updates.push(importSource);
					}
					else if (logging) console.debug('⮂ IMPORTSOURCE_SAVE_MANY: skipping deleted importSource not in state');
				}
				else {
					if (importSource.addedOn >= existing.addedOn) {
						st.set(importSource);
						updates.push(importSource);
					}
					else if (logging) console.debug('⮂ Skipping, existing importSource had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.IMPORTSOURCE_REMOVE: {
			// clone the importSource
			const importSource = new ImportSource({...action.importSource});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(importSource);

			return updateStorage(st, [importSource]);
		}

		case Actions.IMPORTSOURCE_RESTORE_FAILURE:
		case Actions.IMPORTSOURCE_SAVE_FAILURE: {
		    console.warn('IMPORTSOURCE_SAVE_FAILURE reducer', action.importSource?.id);
		    if (!action.importSource?.id) {
				console.warn('IMPORTSOURCE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const importSource = ensure(ImportSource, action.importSource);
			if (!importSource) {
				console.error('Could not find importSource after call to save');
				return state;
			}

			st = state.clone();

			const _importSourceRef = state.get(importSource.id);

			if (_importSourceRef && (_importSourceRef.addedOn === importSource.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _importSource = new ImportSource({..._importSourceRef});
				_importSource.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_importSource.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('importSource', _importSource).then(r => {
						if (r.isErr()) console.error(`⮂ importSourceReducer: failed to save importSource with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ importSourceReducer: saved importSource with fatalError info');
					}).catch(err => console.error(`⮂ importSourceReducer: failed to save importSource with fatalError info: ${err.message}`));

				st.set(_importSource);
			}

			return updateStorage(st);
		}

		case Actions.IMPORTSOURCE_SAVE_MANY_FAILURE: {
		    console.warn('IMPORTSOURCE_SAVE_MANY_FAILURE reducer', action.importSources.map(importSource => importSource.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.IMPORTSOURCE_RESTORE_SUCCESS:
		case Actions.IMPORTSOURCE_SAVE_SUCCESS: {
			if (!action.importSource?.id) {
				console.warn('IMPORTSOURCE_SAVE_SUCCESS reducer');
                return state;
			}

			const importSourceRef = state.get(action.importSource?.id);
			if (!importSourceRef) {
				console.warn('Could not find importSource after call to save');
                return state;
			}

			st = state.clone();

			if (action.importSource.addedOn >= importSourceRef.addedOn) {
				st.set(action.importSource);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const importSource = new ImportSource({...importSourceRef, persisted: true});
				st.set(importSource);
			}

			return updateStorage(st, [action.importSource]);
		}

		case Actions.IMPORTSOURCE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ IMPORTSOURCE_SAVE_MANY_SUCCESS reducer', action.importSources.map(importSource => importSource.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const importSources: ImportSource[] = [];

			action.importSources.forEach(e => {
				const importSourceRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!importSourceRef) console.warn('Could not find importSource after call to save');
				else if (e.addedOn >= importSourceRef.addedOn) {
				    st.set(e);
				    importSources.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const importSource = new ImportSource({...importSourceRef, persisted: true});
					st.set(importSource);
					importSources.push(importSource);
				}
			});

			return updateStorage(st, importSources);
		}

		case Actions.IMPORTSOURCE_REMOVE_FAILURE: {
		    console.warn('IMPORTSOURCE_REMOVE_FAILURE action', action.importSource?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.IMPORTSOURCE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.IMPORTSOURCE_RESTORE_MANY: {
			if (logging) console.debug('⮂ IMPORTSOURCE_RESTORE_MANY reducer', action.importSources.map(importSource => importSource.id).join(','));

			const toProcess = action.importSources.map(importSource => new ImportSource({...importSource}));
			st = state.clone();

			const updates: ImportSource[] = [];

			toProcess.forEach(importSource => {
				const existing = st.get(importSource.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (importSource.addedOn >= existing.addedOn) {
						st.set(importSource);
						updates.push(importSource);
					}
					else if (logging) console.debug('⮂ IMPORTSOURCE_RESTORE_MANY: skipping older version of importSource');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (importSource.status !== 'D') {
						st.set(importSource);
						updates.push(importSource);
					}
					else if (logging) console.debug('⮂ IMPORTSOURCE_RESTORE_MANY: skipping deleted importSource not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.IMPORTSOURCE_REMOVE_MANY: {
			if (logging) console.debug('⮂ IMPORTSOURCE_REMOVE_MANY reducer', action.importSources.map(importSource => importSource.id).join(','));

			const toProcess = action.importSources.map(importSource => new ImportSource({...importSource}));
			st = state.clone();

			toProcess.forEach(importSource => {
				st.delete(importSource);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default importSources;